// Decode information from vector-tile features so clicking anywhere on the
// map can describe what is there. Works with the CARTO / OpenMapTiles schema
// (source layers: poi, place, transportation_name, water_name, park,
//  mountain_peak, housenumber, building, aerodrome_label).

import type { PlaceInfo } from '../components/map/PlaceInfoCard';

interface FeatureLike {
  properties?: Record<string, any> | null;
  layer?: { id?: string };
  geometry?: { type?: string; coordinates?: any };
}

type TileKind = PlaceInfo['kind'];

interface KindSpec {
  kind: TileKind;
  /** OSM class values that indicate this kind */
  classes: string[];
  /** tile source-layer to read from when the style does not render it */
  sourceLayer: string;
  /** minimum zoom before we bother looking */
  minZoom: number;
}

const KINDS: KindSpec[] = [
  { kind: 'poi', classes: [], sourceLayer: 'poi', minZoom: 13 },
  { kind: 'place', classes: ['city', 'town', 'village', 'hamlet', 'suburb', 'neighbourhood', 'country', 'state'], sourceLayer: 'place', minZoom: 4 },
  { kind: 'aerodrome', classes: ['aerodrome', 'airport', 'heliport'], sourceLayer: 'aerodrome_label', minZoom: 8 },
  { kind: 'peak', classes: [], sourceLayer: 'mountain_peak', minZoom: 8 },
  { kind: 'park', classes: ['park', 'park', 'garden', 'forest', 'wood', 'nature_reserve', 'protected_area', 'national_park', 'golf'], sourceLayer: 'park', minZoom: 11 },
  { kind: 'water', classes: ['ocean', 'sea', 'lake', 'river', 'stream', 'bay', 'reservoir', 'canal', 'pond', 'lagoon'], sourceLayer: 'water_name', minZoom: 6 },
  { kind: 'road', classes: [], sourceLayer: 'transportation_name', minZoom: 12 },
  { kind: 'house', classes: [], sourceLayer: 'housenumber', minZoom: 16 },
  { kind: 'building', classes: [], sourceLayer: 'building', minZoom: 15 },
];

/** Human-friendly label for an OSM class/subclass pair. */
export function humanizeClass(cls?: string, sub?: string): string {
  const pick = (v?: string) => {
    if (!v) return '';
    const s = v.replace(/_/g, ' ').trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const a = pick(cls);
  const b = pick(sub);
  if (a && b && a.toLowerCase() !== b.toLowerCase()) return `${a} · ${b}`;
  return a || b;
}

/** Pick the best display name from the many localized name fields. */
export function pickName(props: Record<string, any>): string | null {
  const candidates = [
    props['name:en'],
    props.name_en,
    props['name:latin'],
    props.name,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return null;
}

function coordOf(feature: FeatureLike): { lng: number; lat: number } | null {
  // queryRenderedFeatures returns geometry for point features; for lines and
  // polygons we fall back to the click point supplied by the caller.
  const g = feature.geometry;
  if (g && g.type === 'Point' && Array.isArray(g.coordinates)) {
    return { lng: g.coordinates[0], lat: g.coordinates[1] };
  }
  return null;
}

/**
 * Turn a raw tile feature into PlaceInfo, or null when it carries no
 * useful information (e.g. an unnamed building).
 */
export function toPlaceInfo(
  feature: FeatureLike,
  fallback: { lng: number; lat: number },
  sourceLayerHint?: string
): PlaceInfo | null {
  const props = feature.properties ?? {};
  const sourceLayer =
    sourceLayerHint ?? feature.layer?.id?.replace(/^(poi|place|road)_?.*$/, '$1') ?? '';

  const name = pickName(props);

  // --- address / housenumber ---
  if (sourceLayer === 'housenumber' || props.housenumber) {
    const num = props.housenumber || props.name;
    if (!num) return null;
    return {
      kind: 'house',
      name: String(num),
      category: 'address',
      lng: fallback.lng,
      lat: fallback.lat,
    };
  }

  // --- POI ---
  const isPoi =
    sourceLayer === 'poi' ||
    (sourceLayer.startsWith('poi') && (props.class || props.subclass));

  if (isPoi) {
    if (!name) return null;
    const cls = typeof props.class === 'string' ? props.class : undefined;
    const sub = typeof props.subclass === 'string' ? props.subclass : undefined;

    const extras: { label: string; value: string }[] = [];
    if (typeof props.indoor === 'boolean' && props.indoor) extras.push({ label: 'Location', value: 'Indoor' });
    if (typeof props.level === 'string' && props.level) extras.push({ label: 'Level', value: props.level });
    if (typeof props.agg_stop === 'string' && props.agg_stop) extras.push({ label: 'Transit', value: props.agg_stop });

    const c = coordOf(feature) ?? fallback;
    return {
      kind: 'poi',
      name,
      category: cls ?? sub ?? 'place',
      subclass: sub,
      osmClass: cls,
      lng: c.lng,
      lat: c.lat,
      extras,
    };
  }

  // --- named place (city, town, neighbourhood...) ---
  if (sourceLayer === 'place' || sourceLayer.startsWith('place')) {
    if (!name) return null;
    const cls = typeof props.class === 'string' ? props.class : undefined;
    const c = coordOf(feature) ?? fallback;
    return {
      kind: 'place',
      name,
      category: cls ?? 'place',
      subclass: typeof props.subclass === 'string' ? props.subclass : undefined,
      osmClass: cls,
      lng: c.lng,
      lat: c.lat,
    };
  }

  // --- airport ---
  if (sourceLayer === 'aerodrome_label') {
    if (!name) return null;
    const extras: { label: string; value: string }[] = [];
    if (typeof props.iata === 'string' && props.iata) extras.push({ label: 'IATA', value: props.iata });
    if (typeof props.icao === 'string' && props.icao) extras.push({ label: 'ICAO', value: props.icao });
    const c = coordOf(feature) ?? fallback;
    return {
      kind: 'aerodrome',
      name,
      category: typeof props.class === 'string' ? props.class : 'airport',
      lng: c.lng,
      lat: c.lat,
      extras,
    };
  }

  // --- mountain peak ---
  if (sourceLayer === 'mountain_peak') {
    if (!name) return null;
    const extras: { label: string; value: string }[] = [];
    if (typeof props.ele === 'number') extras.push({ label: 'Elevation', value: `${props.ele.toLocaleString()} m` });
    const c = coordOf(feature) ?? fallback;
    return {
      kind: 'peak',
      name,
      category: typeof props.class === 'string' ? props.class : 'peak',
      lng: c.lng,
      lat: c.lat,
      extras,
    };
  }

  // --- park / green space ---
  if (sourceLayer === 'park') {
    if (!name) return null;
    const c = coordOf(feature) ?? fallback;
    return {
      kind: 'park',
      name,
      category: typeof props.class === 'string' ? props.class : 'park',
      lng: c.lng,
      lat: c.lat,
    };
  }

  // --- water ---
  if (sourceLayer === 'water_name') {
    if (!name) return null;
    const c = coordOf(feature) ?? fallback;
    return {
      kind: 'water',
      name,
      category: typeof props.class === 'string' ? props.class : 'water',
      lng: c.lng,
      lat: c.lat,
    };
  }

  // --- road / street ---
  if (sourceLayer.startsWith('transportation') || sourceLayer.startsWith('road')) {
    if (!name) return null;
    const extras: { label: string; value: string }[] = [];
    if (typeof props.ref === 'string' && props.ref) extras.push({ label: 'Ref', value: props.ref });
    if (typeof props.network === 'string' && props.network) extras.push({ label: 'Network', value: props.network });
    return {
      kind: 'road',
      name,
      category: typeof props.class === 'string' ? props.class : 'road',
      subclass: typeof props.subclass === 'string' ? props.subclass : undefined,
      osmClass: typeof props.class === 'string' ? props.class : undefined,
      lng: fallback.lng,
      lat: fallback.lat,
      extras,
    };
  }

  // --- building (only if it has a name; otherwise it is not worth a popup) ---
  if (sourceLayer === 'building') {
    if (!name) return null;
    const c = coordOf(feature) ?? fallback;
    return {
      kind: 'building',
      name,
      category: typeof props.class === 'string' ? props.class : 'building',
      lng: c.lng,
      lat: c.lat,
    };
  }

  // Fall back to a name-only summary for anything else the style renders.
  if (name && (sourceLayer.startsWith('poi') || sourceLayer.startsWith('place'))) {
    const c = coordOf(feature) ?? fallback;
    return {
      kind: 'poi',
      name,
      category: typeof props.class === 'string' ? props.class : 'place',
      lng: c.lng,
      lat: c.lat,
    };
  }

  return null;
}

/**
 * Choose the most interesting feature from a set returned by
 * map.queryRenderedFeatures(). Priority follows KINDS order.
 */
export function pickBestPlace(
  features: FeatureLike[],
  point: { lng: number; lat: number }
): PlaceInfo | null {
  const candidates: { info: PlaceInfo; rank: number }[] = [];

  for (const f of features) {
    // Forward the source-layer hint so the decoder knows what kind of
    // feature this is (the raw tile feature has no layer metadata).
    const info = toPlaceInfo(f, point, (f as any).sourceLayerHint);
    if (!info) continue;
    const idx = KINDS.findIndex((k) => k.kind === info.kind);
    candidates.push({ info, rank: idx === -1 ? KINDS.length : idx });
  }

  if (!candidates.length) return null;
  candidates.sort((a, b) => a.rank - b.rank);
  return candidates[0].info;
}

/** Source layers that must be queried at the current zoom. */
export function sourceLayersForZoom(zoom: number): string[] {
  return KINDS.filter((k) => zoom >= k.minZoom).map((k) => k.sourceLayer);
}